/**
 * Sourced from Dashboard, modified for Shop.
 * https://github.com/squareup/dashboard/blob/49a226ece70f193b59011027ec6388f371f7f7b9/frontend/dashboard/app/helpers/user-journey/user-journey-result.js
 */

/**
 * @typedef {string} TUserJourneyName
 */

/**
 * Enum for the different user journeys that we want to track.
 * @enum {TUserJourneyName}
 * @readonly
 */
export const UserJourneyName = {
  ShopAddItemToCart: 'shop-add-item-to-cart',
  ShopPlaceOrder: 'shop-place-order',
  ShopReturnItem: 'shop-return-item',
  ShopFulfillWarranty: 'shop-fulfill-warranty',
};
Object.freeze(UserJourneyName);

/**
 * @typedef {string} TUserJourneyVariant
 */

/**
 * Variants are optional, but help with segmenting different metrics or SLOs
 * based on different paths or configurations in your User Journey.
 * For example, login uses the auth method as a variant because SMS 2FA will
 * always have a longer duration than other auth methods.
 * https://docs.google.com/document/d/14Fgr3GJw74dyM_Y6i3lhQJmqlO6j68KMtur0Vksg-H8/edit#heading=h.2spye7p3r0uc
 * @enum {TUserJourneyVariant}
 * @readonly
 */
export const UserJourneyVariant = {
  UpfrontOrder: 'upfront',
  InstallmentOrder: 'installment',
  PurchaseOrder: 'purchase-order',
};
Object.freeze(UserJourneyVariant);

export const UserJourneyVariantKeyByValue = (value) => {
  const variantKey = Object.keys(UserJourneyVariant).find(
    (key) => UserJourneyVariant[key] === value.replaceAll(/[\s_]/g, '-'),
  );

  return UserJourneyVariant[variantKey];
};

/**
 * @typedef {string} TFrustrationSignal
 */

/**
 * This is an optional step, but very powerful to add signals of known bad
 * experiences or errors that need to be monitored. We currently use frustration
 * signals to classify a bad experience in a user journey and form SLO
 * thresholds on successful outcomes with no frustration signals.
 * https://docs.google.com/document/d/14Fgr3GJw74dyM_Y6i3lhQJmqlO6j68KMtur0Vksg-H8/edit#heading=h.e9e2y3ko8bhd
 * @enum {TFrustrationSignal}
 * @readonly
 */
export const FrustrationSignal = {
  CashAppPayStepOrderRefreshError: 'cash-app-pay-step-order-refresh-error',
  CompleteStepOrderRefreshError: 'complete-step-order-refresh-error',
  FinanceAppStepApplicationFlowSubmitError:
    'finance-app-step-application-flow-submit-error',
  InformationStepOrderRefreshError: 'information-step-order-refresh-error',
  InformationStepUnknownError: 'information-step-unknown-error',
  OfferSelectStepOfferSelectError: 'offer-select-step-offer-select-error',
  OfferSelectStepOffersFetchError: 'offer-select-step-offers-fetch-error',
  PaymentOptionStepOfferSelectError: 'payment-option-step-offer-select-error',
  PaymentOptionStepPaymentOptionsFetchError:
    'payment-option-step-payment-options-fetch-error',
  PaymentOptionStepPaymentSelectError:
    'payment-option-step-payment-select-error',
  PaymentStepPaymentCreateError: 'payment-step-payment-create-error',
  PaymentStepUnknownError: 'payment-step-unknown-error',
  ReviewStepFinancingDisclosuresCreateError:
    'review-step-financing-disclosures-create-error',
  ReviewStepOrderCompletionError: 'review-step-order-completion-error',
  ShippingMethodStepShippingRateSelectError:
    'shipping-method-step-shipping-rate-select-error',
  ShippingMethodStepShippingRatesFetchError:
    'shipping-method-shipping-rates-fetch-error',
};
Object.freeze(FrustrationSignal);

/**
 * @typedef {string} TFrictionSignal
 */

/**
 * Friction signals are optional but help detect and illuminate issues the
 * seller might be experiencing during the User Journey.
 * https://docs.google.com/document/d/14Fgr3GJw74dyM_Y6i3lhQJmqlO6j68KMtur0Vksg-H8/edit#heading=h.2ekbx1wz5uzr
 * @enum {TFrictionSignal}
 * @readonly
 */
export const FrictionSignal = {
  PaymentStepPaymentInformationIncorrect:
    'payment-step-payment-information-incorrect', // e.g. invalid card number
  PaymentStepPaymentFormStepMissingError:
    'payment-step-payment-form-step-missing-error', // e.g. not checking ToS checkbox
  ReviewStepOrderCompletionPaymentError: 'review-step-payment-error', // e.g., payment declined, card details missing, etc.
  ShippingMethodStepOrderRefreshError:
    'shipping-method-step-order-refresh-error', // order refresh error with minimal impact for users
};
Object.freeze(FrictionSignal);

/**
 * @typedef {string} TUserJourneyOutcome
 */

/**
 * Enum for the different outcomes of a user journey.
 * https://github.com/squareup/java/blob/master/user-journeys/src/main/java/com/squareup/userjourneys/domainmodels/UserJourneyResult.kt#L154
 * @enum {TUserJourneyOutcome}
 * @readonly
 */
export const UserJourneyOutcome = {
  SUCCEEDED: 'SUCCEEDED',
  USER_CANCELED: 'USER_CANCELED',
  TERMINATED: 'TERMINATED',
};
Object.freeze(UserJourneyOutcome);

/**
 * @typedef {string} TTerminationReason
 */

/**
 * Enum for the different reasons a user journey might be terminated.
 * https://github.com/squareup/java/blob/master/user-journeys/src/main/java/com/squareup/userjourneys/domainmodels/UserJourneyResult.kt#L183
 * @enum {TTerminationReason}
 * @readonly
 */
export const TerminationReason = {
  // TODO: implement timeout off-ramps? the doc go/uj-how-to defines timeout as 10min
  TIMEOUT_WITH_ACTIVE_USER: 'TIMEOUT_WITH_ACTIVE_USER',
  TIMEOUT_WITH_IDLE_USER: 'TIMEOUT_WITH_IDLE_USER',
  JOURNEY_RESTARTED: 'JOURNEY_RESTARTED',
};
Object.freeze(TerminationReason);
